import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { useCssStyle } from '../../customHook/getCssStyle';
import HeaderWithBack from '../../components/Header/headerWithBack';
import { ImageComp } from '../../components/Image/Image';
import CustomPressable from '../../components/customPressable/customPressable';
import moment from 'moment';
import DatePickerModal from '../../components/datePickerModal/datePickerModal';
import { useDispatch, useSelector } from 'react-redux';
import { getCleanUrl } from '../../utils/Helper';
import { CustomTextInput } from '../../components/customTextInput';
import { sendAnalytics } from '../../Redux/Actions/analyticsAction';
import { MOBILE_CHECKIN_CATEGORY } from '../../config/Constant';
import { formatDate } from '../../js/formatDate';
import CustomDropDown from '../../components/customDropdown/customDropdown';
import { setTempSelectedHotel } from '../../Redux/Reducers/appConfigReducer';

const BookingInfo = ({ checkinSessionToken, guestdetails = {}, isConfirmationNumberMandatory, handleGoBack, handleCancel, handleNext }) => {


  const dispatch = useDispatch();


  const [lastName, setLastName] = useState(guestdetails.lastName || '');
  const [checkinDate, setCheckInDate] = useState(guestdetails.checkinDate || '');
  const [confirmationNumber, setConfirmationNumber] = useState(guestdetails.confirmationNumber || '');
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false);

  const { tempSelectedHotel } = useSelector(state => state.appConfigSlice);
  const { serverTimeDataObj } = useSelector(state => state.serverDateTimeSlice)
  const { currentWidget, extraConfigParam, webAppWidgetsInfo } = useSelector((state) => state.deviceConfigSlice);
  const dateFormat = extraConfigParam?.DATE_FORMAT;
  const preCheckInTimeLimit = webAppWidgetsInfo?.mobileCheckInConfigs?.preCheckInTimeLimit || undefined

  const { allowUserSelectLocation } = webAppWidgetsInfo?.appConfigs || {};

  const { hotelListData } = useSelector(state => state.hotelSelectorSlice);
  const hotelListsInfo = hotelListData?.payload?.data;

  const { t } = useTranslation();
  const getStyles = useCssStyle();


  const handleTempLocationSelect = (selectedTempLocation) => {
    if (!selectedTempLocation?.value) return;
    
    // Flatten all hotelList arrays and find the hotel by hotelPropertyKey
    const selectedHotel = hotelListsInfo
      ?.flatMap(region => region.hotelList || [])
      .find(hotel => hotel.hotelPropertyKey === selectedTempLocation.value);
  
    dispatch(setTempSelectedHotel(selectedHotel ?? null));
  };

  const handleDateChange = useCallback((selectedDate) => {
    setIsDatePickerVisible(false);
    setCheckInDate(moment(selectedDate).format('YYYY-MM-DD'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVerifyDetails = () => {

    const guestData = {
      lastName,
      confirmationNumber,
      checkinDate
    }

    handleNext(guestData);
  }

  const isFormValid = useMemo(() => lastName && checkinDate && (!isConfirmationNumberMandatory || confirmationNumber) && (!allowUserSelectLocation || tempSelectedHotel?.hotelPropertyKey), [lastName, checkinDate, isConfirmationNumberMandatory, confirmationNumber, allowUserSelectLocation, tempSelectedHotel]);


  useEffect(() => {
    dispatch(sendAnalytics(MOBILE_CHECKIN_CATEGORY, "BOOKING NUMBER", { "element": "widget", "widgetSessionId": checkinSessionToken}));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])


  const renderHeader = () => {


    return (
      <View style={getStyles(['widgetmainContainerHeaderContainer', 'bookingInfoHeaderContainer'])}>
        {
          currentWidget?.widgetHeaderImageUrl &&
          <ImageComp source={getCleanUrl(currentWidget?.widgetHeaderImageUrl)} style={getStyles(['widgetmainContainerHeaderImg', 'bookingInfoHeaderImg'])} />
        }
        <View style={getStyles(['widgetmainContainerHeaderTitleContainer', 'bookingInfoHeaderTitleContainer'])}>
          <Text style={getStyles(['widgetmainContainerHeaderTitle', 'bookingInfoHeaderTitle'])}>{t('check_in.bookingInfo.heading')}</Text>
          <Text style={getStyles(['widgetmainContainerHeaderSubTitle', 'bookingInfoHeaderSubTitle'])}>{t('check_in.bookingInfo.sub_heading')}</Text>
        </View>
      </View>
    )
  }

  return (
    <View style={getStyles(['widgetRootContainer', 'bookingInfoRootContainer'])} >
      <HeaderWithBack handleCustomBackPress={handleGoBack} handleCustomCancelClick={handleCancel}/>

      <ScrollView automaticallyAdjustKeyboardInsets={true} contentContainerStyle={getStyles(['widgetmainContainer', 'bookingInfoMainContainer'])} showsVerticalScrollIndicator={false}>
        {renderHeader()}
        <View style={getStyles(['widgetmainContainerChildContainer', 'bookingInfoMainContainerChildContainer'])}>


          {allowUserSelectLocation && <CustomDropDown
            label={t('check_in.bookingInfo.select_hotel')}
            placeholder={t('check_in.bookingInfo.select_hotel_placeholder')}
            isRequired={true}
            options={hotelListsInfo?.flatMap(item => [
              { type: 'header', label: item.region, value: null },  // heading item
              ...item.hotelList?.map(hotel => ({
                type: 'item',
                label: hotel.hotelName,
                value: hotel.hotelPropertyKey,
              })) || [],
            ])}
            selectedOption={tempSelectedHotel?.hotelPropertyKey}
            onSelectOption={handleTempLocationSelect}
          />}


          <View style={getStyles(['textContainer'])}>
            <Text style={getStyles(['label'])}>{t('check_in.bookingInfo.last_name')} <Text style={getStyles(['requiredText'])}>*</Text></Text>
            <CustomTextInput
              value={lastName}
              onChangeText={setLastName}
              placeholder={t('check_in.bookingInfo.last_name_placeholder')}
              autocomplete={"family-name"}
            />
          </View>

          {isConfirmationNumberMandatory &&
            <View style={getStyles(['textContainer'])}>
              <Text style={getStyles(['label'])}>{t('check_in.bookingInfo.confirmation_no')} <Text style={getStyles(['requiredText'])}>*</Text></Text>
              <CustomTextInput
                value={confirmationNumber}
                onChangeText={setConfirmationNumber}
                placeholder={t('check_in.bookingInfo.confirmation_no_placeholder')}
              />
            </View>
          }

          <View style={getStyles(['textContainer'])}>
            <Text style={getStyles(['label'])}>{t('check_in.bookingInfo.checkin_date')} <Text style={getStyles(['requiredText'])}>*</Text></Text>

            <CustomPressable onPress={() => setIsDatePickerVisible(true)}>
              <CustomTextInput
                value={checkinDate ? formatDate(checkinDate, dateFormat) : ""}
                placeholder={t('check_in.bookingInfo.checkin_date_placeholder')}
                editable={false}
                pointerEvents="none"
              />
            </CustomPressable>
          </View>

          <Text style={getStyles(['requiredText'])}>{t('check_in.bookingInfo.required_warning')}</Text>

          <Text style={getStyles(['bookingInfoMainContainerChildContainer_infotext'])}>{t('check_in.bookingInfo.checkin_note')}</Text>

          <CustomPressable disabled={!isFormValid} style={getStyles(['primaryBtn'])} onPress={handleVerifyDetails}>
            <Text style={getStyles(['primaryBtnTxt'])}>{t('check_in.bookingInfo.primary_button')}</Text>
          </CustomPressable>

        </View>
      </ScrollView>

      {isDatePickerVisible &&
        <DatePickerModal
          selectedDate={checkinDate}
          onDateChange={handleDateChange}
          onClose={() => setIsDatePickerVisible(false)}
          maxDate={parseInt(preCheckInTimeLimit) > -1 ? new Date(new Date().getTime(serverTimeDataObj?.date?.split("T")[0] + "T00:00:00") + (Math.floor(parseInt(preCheckInTimeLimit) / 24)) * 24 * 60 * 60 * 1000) : null}
        />
      }
    </View>
  );
}; 

export default BookingInfo;
